import { useEffect, useState } from 'react';
import { Clapperboard, ChevronRight, X, Loader2, Zap, AlertCircle, Play } from 'lucide-react';
import { labApi } from '@/lib/api';
import { AvatarPicker, VoicePicker, type ProductionAvatarOption, type ProductionVoiceOption } from './ProductionPickers';

export interface VideoStrategyOption {
  key: string;
  label: string;
  description: string;
  pipeline: string;
  productionProfile: string;
}

export interface VideoStrategyProduction {
  voiceId?: string;
  avatarId?: string;
  voiceSource?: 'heygen' | 'elevenlabs';
}

export interface VideoStructureOption {
  key: string;
  strategyKey: string;
  label: string;
  description: string;
}

interface VideoStrategyPickerModalProps {
  strategies: VideoStrategyOption[];
  structures: VideoStructureOption[];
  onSelect: (strategy: VideoStrategyOption, autoChain: boolean, production: VideoStrategyProduction | null, structure?: string | null) => void;
  onClose: () => void;
}

const PRODUCTION_PROFILE_LABELS: Record<string, string> = {
  heygen: 'Produkcja: wideo UGC (HeyGen)',
  elevenlabs: 'Produkcja: lektor (ElevenLabs) + b-rolle',
  none: 'Materiał tekstowy',
};

export function VideoStrategyPickerModal({ strategies, structures, onSelect, onClose }: VideoStrategyPickerModalProps) {
  const [autoChain, setAutoChain] = useState(false);
  // Tryb AUTO: klik strategii zaznacza ją, a start jest po skonfigurowaniu produkcji
  const [selectedStrategy, setSelectedStrategy] = useState<VideoStrategyOption | null>(null);
  // Krok schematu struktury: strategia z dostępnymi schematami pokazuje karty
  // (wybór idzie jako pre-answer zamiast kroku choice w pipeline)
  const [structureStep, setStructureStep] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState<string | null>(null);
  const [produceEnabled, setProduceEnabled] = useState(true);
  const [voices, setVoices] = useState<ProductionVoiceOption[]>([]);
  const [avatars, setAvatars] = useState<ProductionAvatarOption[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsWarnings, setOptionsWarnings] = useState<string[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Audio-first: głos ZAWSZE z ElevenLabs (jeden głos w całej kreacji),
  // avatary z HeyGen tylko dla profilu wideo
  const needsAvatar = selectedStrategy?.productionProfile === 'heygen';
  const productionApplies = autoChain && selectedStrategy && selectedStrategy.productionProfile !== 'none';

  // Głosy/avatary dla wybranej strategii (pełne listy z API providera)
  useEffect(() => {
    if (!productionApplies || !produceEnabled) return;
    let cancelled = false;
    setLoadingOptions(true);
    setOptionsError(null);
    (async () => {
      try {
        const [voicesRes, avatarsRes] = await Promise.all([
          labApi.getVideoProductionOptions('elevenlabs'),
          needsAvatar ? labApi.getVideoProductionOptions('heygen') : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const voiceData = voicesRes.data.data || {};
        const avatarData = avatarsRes?.data.data || {};
        setVoices(voiceData.voices || []);
        setAvatars(avatarData.avatars || []);
        setOptionsWarnings(avatarData.warnings || []);
        if ((voiceData.voices || []).length > 0) setVoiceId(voiceData.voices[0].voiceId);
        if ((avatarData.avatars || []).length > 0) setAvatarId(avatarData.avatars[0].avatarId);
      } catch (err: any) {
        if (!cancelled) {
          setOptionsError(err?.response?.data?.error || 'Nie udało się pobrać głosów/avatarów. Produkcję możesz uruchomić później z panelu zasobów.');
          setProduceEnabled(false);
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productionApplies, produceEnabled, needsAvatar]);

  const structuresForStrategy = (strategy: VideoStrategyOption | null) =>
    strategy ? structures.filter(item => item.strategyKey === strategy.key) : [];

  const handleStrategyClick = (strategy: VideoStrategyOption) => {
    const hasStructures = structuresForStrategy(strategy).length > 0;
    if (hasStructures) {
      setSelectedStrategy(strategy);
      setSelectedStructure(null);
      setStructureStep(true);
      return;
    }
    if (autoChain && strategy.productionProfile !== 'none') {
      setSelectedStrategy(prev => (prev?.key === strategy.key ? prev : strategy));
      return;
    }
    onSelect(strategy, autoChain, null, null);
  };

  const handleStructureClick = (structureKey: string | null) => {
    if (!selectedStrategy) return;
    setSelectedStructure(structureKey);
    // Tryb manualny: wybór schematu = start. AUTO: jeszcze sekcja produkcji poniżej.
    if (!(autoChain && selectedStrategy.productionProfile !== 'none')) {
      onSelect(selectedStrategy, autoChain, null, structureKey);
    }
  };

  const handleAutoStart = () => {
    if (!selectedStrategy) return;
    const production = produceEnabled && voiceId
      ? { voiceId, voiceSource: 'elevenlabs' as const, ...(needsAvatar && avatarId ? { avatarId } : {}) }
      : null;
    onSelect(selectedStrategy, true, production, selectedStructure);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">
            {structureStep && selectedStrategy ? `Schemat skryptu: ${selectedStrategy.label}` : 'Wybierz strategię reklamy wideo'}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!structureStep && (
          <p className="text-xs text-text-muted mb-3">
            Strategia określa metodologię skryptu. Rezultatem jest skrypt-matka, z którego wygenerujesz kolejne zasoby produkcyjne (sceny, b-rolle, brief montażysty).
          </p>
        )}
        {structureStep && (
          <button
            onClick={() => {
              setStructureStep(false);
              setSelectedStructure(null);
            }}
            className="text-[11px] text-text-muted hover:text-text-primary transition-colors mb-3"
          >
            ← Wróć do strategii
          </button>
        )}

        <label className="flex items-start gap-2.5 p-3 mb-4 rounded-lg border border-accent-primary/20 bg-accent-primary/5 cursor-pointer">
          <input
            type="checkbox"
            checked={autoChain}
            onChange={e => {
              setAutoChain(e.target.checked);
              if (!e.target.checked) setSelectedStrategy(null);
            }}
            className="mt-0.5 accent-current"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-xs font-medium text-text-primary">
              <Zap className="w-3.5 h-3.5 text-accent-primary" /> Tryb AUTO
            </span>
            <span className="block text-[11px] text-text-muted mt-0.5 leading-relaxed">
              Zero klikania: AI sam wybiera rekomendowane opcje (angle, hook, lead, struktura), po skrypcie automatycznie generuje sceny, b-rolle i brief montażysty, a na końcu produkuje wideo/audio z wybranym poniżej głosem i avatarem.
            </span>
          </span>
        </label>
        {structureStep && selectedStrategy ? (
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            <button
              onClick={() => handleStructureClick(null)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                selectedStructure === null && autoChain
                  ? 'bg-accent-primary/5 border-accent-primary/40'
                  : 'bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80'
              }`}
            >
              <Zap className="w-5 h-5 shrink-0 mt-0.5 text-accent-primary" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-primary">Rekomendacja AI</div>
                <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed">
                  AI przeanalizuje produkt i grupę docelową, po czym samo dobierze najlepszy schemat.
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-disabled shrink-0 mt-0.5" />
            </button>
            {structuresForStrategy(selectedStrategy).map(structure => {
              const isSelected = selectedStructure === structure.key;
              return (
                <button
                  key={structure.key}
                  onClick={() => handleStructureClick(structure.key)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                    isSelected
                      ? 'bg-accent-primary/5 border-accent-primary/40'
                      : 'bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80'
                  }`}
                >
                  <Clapperboard className={`w-5 h-5 shrink-0 mt-0.5 ${isSelected ? 'text-accent-primary' : 'text-accent-info'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">{structure.label}</div>
                    <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{structure.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-disabled shrink-0 mt-0.5" />
                </button>
              );
            })}
          </div>
        ) : strategies.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-8 text-xs text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Ładowanie strategii...
          </div>
        ) : (
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            {strategies.map(strategy => {
              const isSelected = selectedStrategy?.key === strategy.key;
              return (
                <button
                  key={strategy.key}
                  onClick={() => handleStrategyClick(strategy)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                    isSelected
                      ? 'bg-accent-primary/5 border-accent-primary/40'
                      : 'bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80'
                  }`}
                >
                  <Clapperboard className={`w-5 h-5 shrink-0 mt-0.5 ${isSelected ? 'text-accent-primary' : 'text-accent-info'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">{strategy.label}</div>
                    <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{strategy.description}</div>
                    <div className="text-[10px] text-accent-info/80 mt-1">
                      {PRODUCTION_PROFILE_LABELS[strategy.productionProfile] || strategy.productionProfile}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-disabled shrink-0 mt-0.5" />
                </button>
              );
            })}
          </div>
        )}

        {/* AUTO: konfiguracja produkcji dla wybranej strategii */}
        {productionApplies && (
          <div className="mt-4 p-3 rounded-lg border border-border bg-surface-hover/30 space-y-3">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={produceEnabled}
                onChange={e => setProduceEnabled(e.target.checked)}
                className="mt-0.5 accent-current"
              />
              <span>
                <span className="text-xs font-medium text-text-primary block">
                  Wyprodukuj też {selectedStrategy!.productionProfile === 'heygen' ? 'wideo UGC (HeyGen)' : 'audio lektora (ElevenLabs)'}
                </span>
                <span className="text-[11px] text-text-muted block mt-0.5">
                  Plik trafi na Google Drive. Bez tego łańcuch kończy się na zasobach tekstowych.
                </span>
              </span>
            </label>

            {produceEnabled && (loadingOptions ? (
              <div className="flex items-center gap-2 py-2 text-xs text-text-muted">
                <Loader2 className="w-4 h-4 animate-spin" /> Pobieranie głosów{needsAvatar ? ' i avatarów' : ''}...
              </div>
            ) : (
              <div className="space-y-3">
                {needsAvatar && (
                  <div>
                    <span className="text-xs font-medium text-text-secondary mb-1.5 block">Avatar</span>
                    <AvatarPicker idPrefix="auto" avatars={avatars} value={avatarId} onChange={setAvatarId} />
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-text-secondary mb-1.5 block">Głos (ElevenLabs - ten sam w całej kreacji)</span>
                  <VoicePicker idPrefix="auto" voices={voices} value={voiceId} onChange={setVoiceId} />
                </div>
              </div>
            ))}

            {optionsWarnings.map((warning, index) => (
              <div key={index} className="flex items-start gap-2 text-[11px] text-warning">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {warning}
              </div>
            ))}

            {optionsError && (
              <div className="flex items-center gap-2 text-[11px] text-warning">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {optionsError}
              </div>
            )}

            <button
              onClick={handleAutoStart}
              disabled={loadingOptions || (produceEnabled && (!voiceId || (needsAvatar && !avatarId)))}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
            >
              <Play className="w-3.5 h-3.5" />
              Start AUTO: {selectedStrategy!.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
